import { getCurrentOwnerUid } from "./currentOwnerUid";

const DB_NAME = "clippn-uploads";
const DB_VERSION = 1;
const STORE = "inflightPackages";

// Multi-tenancy scoping (2026-08-05 leak fix). The DB itself is shared across
// every user who signs into this browser (no per-user DB name — that would
// leak in a different direction if a user is signed into two accounts on
// separate tabs). Every record instead carries an ownerUid stamp; reads
// filter by the currently authenticated user via filterRecordsForOwner.
// Pre-migration records have no ownerUid and are hidden (see the "hide
// migration" note on filterRecordsForOwner).

// Stamps the current user's uid onto a record before persistence. Idempotent:
// once a record has an ownerUid it is preserved on subsequent saves so a
// takeover flow (or a save that fires while auth is briefly nulled during a
// token refresh) can't accidentally re-tag another user's record as ours.
// Records with no ownerUid AND no current uid are returned unchanged — this
// keeps them hidden from every subsequent read rather than stamping a null.
export const applyOwnerScope = (record, ownerUid) => {
  if (!record || typeof record !== "object") return record;
  if (record.ownerUid) return record;
  if (!ownerUid) return record;
  return { ...record, ownerUid };
};

// Strict-equality filter used by every list-side read. `ownerUid` mismatches
// (including null/undefined on either side) drop the record. Pre-migration
// records without an ownerUid are hidden rather than adopted — auto-adoption
// during rollout could claim another user's in-flight work as the freshly-
// logged-in user's own.
export const filterRecordsForOwner = (records, ownerUid) => {
  if (!Array.isArray(records)) return [];
  if (!ownerUid) return [];
  return records.filter((r) => r?.ownerUid === ownerUid);
};

// Backend-verified adoption (2026-08-05 follow-up). Hide-migration correctly
// prevents cross-user leakage but also hides the rightful owner's own
// pre-fix records. When some external source of truth (backend
// fetchActiveSessions or verifyUploadStatus) has confirmed an uploadId
// belongs to the current user, this pure helper decides whether to stamp
// the record. Never overwrites an existing ownerUid — that's the safety
// invariant that stops a rogue "adopt-everything" from re-introducing the
// original leak.
export const stampOwnerUidIfOrphan = (record, currentUid) => {
  if (!record) return record;
  if (record.ownerUid) return record;
  if (!currentUid) return record;
  return { ...record, ownerUid: currentUid };
};

// Custom-event bridge used by orphan reconciliation in UploadProgressLoadingCard to nudge
// ResumePreviousUploadBanner to re-run loadPending. Needed when the sibling tab that owned
// the upload died while this tab was already open: our IndexedDB record is now the only
// source of the paused-package data, but our earlier boot-time rehydration ran before that
// record was written. Firing this event triggers a force rescan so the card gains a
// pausedRecord and FS handles, and the Resume click actually starts a new session.
export const REHYDRATION_TRIGGER_EVENT = "clipn-upload-request-rehydration";
export const requestRehydration = () => {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(REHYDRATION_TRIGGER_EVENT));
  } catch {
    // CustomEvent may throw in very old environments — the reconciliation is best-effort.
  }
};

const isSupported = () =>
  typeof indexedDB !== "undefined" && typeof window !== "undefined";

let dbPromise = null;

const openDb = () => {
  if (!isSupported()) return Promise.resolve(null);
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "uploadId" });
      }
    };
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
  });
  return dbPromise.catch((err) => {
    console.warn("[uploadPersistence] IndexedDB unavailable", err);
    dbPromise = null;
    return null;
  });
};

const withStore = async (mode, fn) => {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    let result;
    try {
      result = fn(store);
    } catch (err) {
      reject(err);
      return;
    }
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
};

const asPromise = (request) =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

export const savePendingPackage = async (record) => {
  if (!record?.uploadId) return;
  const scoped = applyOwnerScope(record, getCurrentOwnerUid());
  const toStore = {
    ...scoped,
    persistedAt: Date.now(),
  };
  try {
    await withStore("readwrite", (store) => asPromise(store.put(toStore)));
  } catch (err) {
    console.warn("[uploadPersistence] save failed", err);
  }
};

const getPendingPackage = async (uploadId) => {
  if (!uploadId) return null;
  try {
    return await withStore("readonly", (store) => asPromise(store.get(uploadId)));
  } catch (err) {
    console.warn("[uploadPersistence] get failed", err);
    return null;
  }
};

// Merge-mode update for the paused flag. When pausing, callers can pass a per-file
// bytesUploaded snapshot which is merged into the persisted files manifest — boot
// rehydration uses those values to show the correct progress on the paused card.
//
// `pauseSource` differentiates a user-initiated pause ("user") from an auto-pause
// triggered by tus onError ("auto"). During a page reload the browser aborts every
// in-flight PATCH, tus onError fires with no response, classifyTusError marks it
// transient, and pauseInFlight ends up writing paused:true to IDB — the record
// looks identical to a real user pause on rehydrate. The card's isInterruptedRecord
// check reads pauseSource to tell the two cases apart so the "Upload interrupted
// by page reload" hint keeps rendering after the reload race.
export const markPackagePaused = async (uploadId, paused, fileProgress = null, pauseSource = "user") => {
  const existing = await getPendingPackage(uploadId);
  if (!existing) return;
  const next = {
    ...existing,
    paused: !!paused,
    pausedAt: paused ? Date.now() : null,
    pauseSource: paused ? pauseSource : null,
  };
  if (paused && fileProgress && existing.files) {
    next.files = existing.files.map((f) => ({
      ...f,
      bytesUploaded: fileProgress[f.sourceFileId] ?? f.bytesUploaded ?? 0,
    }));
  }
  await savePendingPackage(next);
};

// Periodic snapshot of per-file bytesUploaded during active upload. Lets a crash/reload
// rehydrate with accurate progress instead of flashing zero until the first HEAD response.
export const persistFileProgress = async (uploadId, fileProgress) => {
  if (!uploadId || !fileProgress) return;
  const existing = await getPendingPackage(uploadId);
  if (!existing?.files) return;
  await savePendingPackage({
    ...existing,
    files: existing.files.map((f) => ({
      ...f,
      bytesUploaded: fileProgress[f.sourceFileId] ?? f.bytesUploaded ?? 0,
    })),
  });
};

// Mark a single file as fully uploaded in the persisted record. Called from tus.onSuccess,
// not from the 5s progress snapshot timer — without this, a reload between file N's onSuccess
// and the next snapshot tick would rehydrate file N as "still uploading" and the resume flow
// would re-upload it from scratch (the server-side TUS session is already gone post-
// finalization, so it'd actually re-send bytes via a brand-new TUS POST).
export const markFileUploaded = async (uploadId, sourceFileId) => {
  if (!uploadId || !sourceFileId) return;
  const existing = await getPendingPackage(uploadId);
  if (!existing?.files) return;
  await savePendingPackage({
    ...existing,
    files: existing.files.map((f) =>
      f.sourceFileId === sourceFileId
        ? { ...f, bytesUploaded: f.sizeBytes ?? f.bytesUploaded ?? 0, chunkStatus: "UPLOADED" }
        : f
    ),
  });
};

export const removePendingPackage = async (uploadId) => {
  if (!uploadId) return;
  clearFsHandlesForUpload(uploadId);
  try {
    await withStore("readwrite", (store) => asPromise(store.delete(uploadId)));
  } catch (err) {
    console.warn("[uploadPersistence] remove failed", err);
  }
};

const readAllRawRecords = async () => {
  try {
    return await withStore("readonly", (store) =>
      new Promise((resolve, reject) => {
        const req = store.getAll();
        req.onsuccess = () => resolve(req.result || []);
        req.onerror = () => reject(req.error);
      })
    ) || [];
  } catch (err) {
    console.warn("[uploadPersistence] list failed", err);
    return [];
  }
};

// Reads every persisted record and filters by the caller's ownerUid. The
// parameter is required (rather than defaulting to the current uid) so
// callers explicitly acknowledge whose records they want — makes the ownership
// contract visible at the callsite instead of implicit through auth state.
// Records belonging to other users, and pre-migration records with no
// ownerUid, are dropped.
export const listPendingPackages = async (ownerUid) => {
  const all = await readAllRawRecords();
  return filterRecordsForOwner(all, ownerUid);
};

// Companion to listPendingPackages: returns pre-migration records that
// have no ownerUid at all. Used by the backend-verified adoption paths
// (fetchActiveSessions middleware, ResumePreviousUploadBanner verify
// fallback) to find records that MIGHT belong to the current user, so
// the backend can be asked whether they actually do.
export const listOrphanPendingPackages = async () => {
  const all = await readAllRawRecords();
  return all.filter((r) => r && !r.ownerUid);
};

// Adopts a single orphan record. Reads by uploadId, stamps ownerUid via the
// pure helper (which no-ops if the record already has an owner), writes
// back. Returns true when an adoption actually happened — callers use that
// to decide whether to trigger a rehydration rescan. Swallows IDB errors;
// a failure just leaves the record hidden until the next adoption attempt.
export const adoptOrphanRecord = async (uploadId, currentUid) => {
  if (!uploadId || !currentUid) return false;
  const existing = await getPendingPackage(uploadId);
  if (!existing) return false;
  if (existing.ownerUid) return false;
  const stamped = stampOwnerUidIfOrphan(existing, currentUid);
  if (stamped === existing) return false;
  await savePendingPackage(stamped);
  return true;
};

// Stable cross-browser resume fingerprint. lastModified is intentionally excluded because
// reselected Files can differ by browser/source even when they are the same original asset.
export const fileFingerprint = (file) =>
  `${file.name}|${file.size}`;

// FileSystem Access API helpers. Chromium-only today (no Safari/Firefox); used as a fast path
// to skip the reselect picker entirely when the user previously granted file access.
export const supportsFsHandles = () =>
  typeof window !== "undefined" &&
  typeof window.showOpenFilePicker === "function";

// showDirectoryPicker lets us open a folder picker without triggering Chrome's
// "Upload N files from this folder to [site]?" bulk-upload confirmation dialog (which fires
// for any <input type="file" webkitdirectory>). Same Chromium-only support matrix as
// showOpenFilePicker, so we fall back to the webkitdirectory <input> on Safari/Firefox.
export const supportsDirectoryPicker = () =>
  typeof window !== "undefined" &&
  typeof window.showDirectoryPicker === "function";

// queryPermission can return "granted" | "prompt" | "denied". "prompt" needs a user gesture,
// so callers should only invoke this inside a click handler — never from boot effects.
export const queryHandlePermission = async (handle) => {
  if (!handle || typeof handle.queryPermission !== "function") return "denied";
  try {
    return await handle.queryPermission({ mode: "read" });
  } catch {
    return "denied";
  }
};

// Reasons fileFromHandle can fail. Callers pattern-match on these to render appropriate UI —
// "permission denied" wants a different message than "the file is no longer on disk".
export const FS_HANDLE_REASON = {
  NO_HANDLE: "no-handle",
  PERMISSION: "permission",
  NOT_FOUND: "not-found",
  FINGERPRINT: "fingerprint",
  UNKNOWN: "unknown",
};

// Resolve a persisted handle back to a File. Returns { file, reason }. `file` is null on
// failure; `reason` is null on success. Splitting it lets the caller distinguish the cases
// (permission vs file gone vs fingerprint drift) instead of swallowing all of them as a
// silent fallback to the picker.
export const fileFromHandle = async (handle, expectedFingerprint) => {
  if (!handle || typeof handle.getFile !== "function") {
    return { file: null, reason: FS_HANDLE_REASON.NO_HANDLE };
  }
  try {
    let perm = await queryHandlePermission(handle);
    if (perm === "prompt" && typeof handle.requestPermission === "function") {
      perm = await handle.requestPermission({ mode: "read" });
    }
    if (perm !== "granted") {
      return { file: null, reason: FS_HANDLE_REASON.PERMISSION };
    }
    let file;
    try {
      file = await handle.getFile();
    } catch (err) {
      // NotFoundError fires when the underlying file was moved, deleted, or — for iCloud
      // Photos with "Optimize Storage" — purged from local disk after we stored the handle.
      if (err?.name === "NotFoundError") {
        return { file: null, reason: FS_HANDLE_REASON.NOT_FOUND };
      }
      return { file: null, reason: FS_HANDLE_REASON.UNKNOWN };
    }
    const expectedKey = expectedFingerprint
      ? expectedFingerprint.split("|").slice(0, 2).join("|")
      : null;
    if (expectedKey && fileFingerprint(file) !== expectedKey) {
      return { file: null, reason: FS_HANDLE_REASON.FINGERPRINT };
    }
    return { file, reason: null };
  } catch {
    return { file: null, reason: FS_HANDLE_REASON.UNKNOWN };
  }
};

// FS handles are not structured-clone-safe inside Redux state (the runtime warns; freezing
// would also throw). We persist them inside IndexedDB but keep the runtime-live references
// in this module-scoped Map, keyed by uploadId. Populated by the boot-time rehydrate effect;
// cleared when the upload's IndexedDB record is removed.
const fsHandleStore = new Map();

export const setFsHandlesForUpload = (uploadId, handlesBySourceFileId) => {
  if (!uploadId || !handlesBySourceFileId) return;
  fsHandleStore.set(uploadId, new Map(Object.entries(handlesBySourceFileId)));
};

export const getFsHandle = (uploadId, sourceFileId) => {
  const handles = fsHandleStore.get(uploadId);
  return handles?.get(sourceFileId) ?? null;
};

export const hasAllFsHandles = (uploadId, sourceFileIds) => {
  if (!uploadId || !sourceFileIds?.length) return false;
  const handles = fsHandleStore.get(uploadId);
  if (!handles) return false;
  return sourceFileIds.every((id) => !!handles.get(id));
};

export const clearFsHandlesForUpload = (uploadId) => {
  if (!uploadId) return;
  fsHandleStore.delete(uploadId);
};
